import { useEffect, useState } from 'react';
import type { BaccaratLegal, BaccaratSide, BaccaratView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, useNarrow } from './common';

export function BaccaratTable() {
  const t = useT();
  const view = useGame((s) => s.view) as BaccaratView;
  const legal = useGame((s) => s.legal) as BaccaratLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const narrow = useNarrow();
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const me = mySeat !== null ? view.players[mySeat] : null;
  const betting = Boolean(myTurn && legal?.kind === 'bet');
  const [side, setSide] = useState<BaccaratSide>('BANKER');
  const [amount, setAmount] = useState<number>(view.config.minBet);
  const myResult = view.lastResult?.seats.find((r) => r.seat === mySeat);
  const dealing = view.phase === 'DEALING';
  const ended = view.phase === 'HAND_END';
  const sideName = (s: BaccaratSide) => (s === 'PLAYER' ? t('bc.player') : s === 'BANKER' ? t('bc.banker') : t('bc.tie'));

  useEffect(() => {
    if (legal?.kind === 'bet') setAmount((a) => Math.max(legal.min, Math.min(a, legal.max)));
  }, [legal, view.handNo]);

  const quick = legal?.kind === 'bet' ? [...new Set([1, 2, 5, 10, 25].map((k) => legal.min * k).filter((v) => v <= legal.max).concat(legal.max))].sort((a, b) => a - b) : [];
  const betsOn = (s: BaccaratSide) => view.players.filter((p) => p && p.status === 'BET' && p.side === s) as NonNullable<BaccaratView['players'][number]>[];
  const pays = (s: BaccaratSide) => (s === 'PLAYER' ? 1 : s === 'BANKER' ? (100 - view.config.commission) / 100 : view.config.tiePays);

  const hand = (s: 'PLAYER' | 'BANKER', cards: string[], totalValue: number) => {
    const won = view.winner === s;
    return (
      <div key={s} className="card glass col" style={{ flex: 1, padding: 8, gap: 6, minWidth: 0, borderColor: won ? 'var(--accent)' : undefined, boxShadow: won ? '0 0 0 2px var(--accent)' : undefined }} aria-label={sideName(s)}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{sideName(s)}</strong>
          <span className="pill">{cards.length > 0 ? totalValue : '–'}</span>
        </div>
        <div style={{ display: 'flex', minHeight: narrow ? 52 : 68 }}>
          {cards.map((c, i) => (
            <div key={c} style={{ marginLeft: i === 0 ? 0 : -14 }}>
              <PlayingCard id={c} size="sm" />
            </div>
          ))}
        </div>
        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('bc.pays', { n: pays(s) })}</span>
      </div>
    );
  };

  const betList = (s: BaccaratSide) => {
    const list = betsOn(s);
    return (
      <div key={s} className="row" style={{ gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', minWidth: 52 }}>{sideName(s)}</span>
        {list.length === 0 ? (
          <span className="dim" style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.45)' }}>–</span>
        ) : (
          list.map((p) => (
            <span key={p.seat} className={`pill ${ended && p.payout > p.bet ? 'accent' : ''}`} style={{ fontSize: 'var(--fs-xs)' }}>
              {p.seat === mySeat ? t('cg.you') : name(p.seat)} · {p.bet}
              {ended && p.payout > 0 ? ` → ${p.payout}` : ''}
            </span>
          ))
        )}
      </div>
    );
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        <div className="casino-layer">
          <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
            {hand('PLAYER', view.player, view.playerTotal)}
            {hand('BANKER', view.banker, view.bankerTotal)}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 8, minHeight: 28, flexWrap: 'wrap' }}>
            {ended && view.winner && (
              <span className="pill accent" role="status">
                {view.winner === 'TIE' ? t('bc.tieWins') : t('ab.wins', { side: sideName(view.winner) })}
                {view.lastResult?.natural ? ` · ${t('bc.natural')}` : ''}
              </span>
            )}
            {ended && myResult && (
              <span className={`pill ${myResult.delta > 0 ? 'accent' : myResult.delta < 0 ? 'danger' : ''}`} role="status">
                {myResult.delta > 0 ? t('ab.youWon', { n: myResult.delta }) : myResult.delta < 0 ? t('ab.youLost', { n: -myResult.delta }) : t('bc.push')}
              </span>
            )}
            {dealing && <span className="center-note">{t('ab.dealing')}</span>}
            {view.phase === 'WAITING' && <span className="center-note">{t('bj.waiting')}</span>}
            {view.phase === 'BETTING' && !myTurn && turn?.seats[0] !== undefined && <span className="center-note">{t('table.waitingFor', { name: name(turn.seats[0]) })}</span>}
          </div>
          <div className="card glass col" style={{ padding: 8, gap: 4 }} aria-label={t('bc.bets')}>
            {(['PLAYER', 'BANKER', 'TIE'] as const).map(betList)}
          </div>
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        {betting && legal?.kind === 'bet' && (
          <div className="bid-picker" role="group" aria-label={t('bj.placeBet')}>
            <div className="bid-head">
              <strong>{t('bj.placeBet')}</strong>
              <span className="dim">{t('bj.limits', { min: legal.min, max: legal.max })}</span>
            </div>
            <div className="row" style={{ gap: 6 }} role="radiogroup" aria-label={t('ab.side')}>
              {(['PLAYER', 'BANKER', 'TIE'] as const).map((s) => (
                <button key={s} role="radio" aria-checked={side === s} className={`btn grow ${side === s ? 'primary' : ''}`} onClick={() => setSide(s)}>
                  {sideName(s)}
                </button>
              ))}
            </div>
            <div className="bid-chips amounts" role="radiogroup" aria-label={t('bj.placeBet')}>
              {quick.map((v) => (
                <button key={v} role="radio" aria-checked={amount === v} className={`bid-chip ${amount === v ? 'on' : ''}`} onClick={() => setAmount(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {legal.canSitOut && (
                <button className="btn grow" disabled={acting} onClick={() => void act({ type: 'sitOut' })}>
                  {t('bj.sitOut')}
                </button>
              )}
              <button className="btn primary grow bid-confirm" disabled={acting} onClick={() => void act({ type: 'bet', side, amount })}>
                {t('ab.bet', { n: amount, side: sideName(side) })}
              </button>
            </div>
          </div>
        )}
        {!betting && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)' }}>
              {me && me.status === 'BET' && me.side ? t('ab.yourBet', { side: sideName(me.side), n: me.bet }) : ended ? t('bj.handOver') : dealing ? t('ab.dealing') : view.phase === 'BETTING' ? t('bj.betting') : t('bj.waiting')}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
